import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Save, ArrowLeft } from "lucide-react";
import Logo from "../components/Logo";
import { api, useAuth } from "../context/AuthContext";

const inputCls =
  "w-full bg-[#0D0C0A] border border-[rgba(201,162,101,0.2)] rounded-xl px-4 py-3 text-sm text-[#FAF7F2] placeholder-[#7A7267] focus:outline-none focus:border-[#C9A265] transition-colors duration-300";

const labelCls = "block text-[10px] uppercase tracking-[0.25em] text-[#7A7267] font-cinzel mb-1.5";

export default function AdminReviews() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState(null);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/reviews")
      .then((r) => setReviews(r.data))
      .catch(() => toast.error("Errore nel caricamento delle recensioni"));
    api.get("/settings")
      .then((r) => setSettings(r.data || {}))
      .catch(() => setSettings({}));
  }, []);

  const update = (fn) =>
    setReviews((prev) => {
      const r = structuredClone(prev);
      fn(r);
      return r;
    });

  const save = async () => {
    setSaving(true);
    try {
      const [reviewsRes] = await Promise.all([
        api.put("/reviews", reviews),
        api.put("/settings", settings),
      ]);
      setReviews(reviewsRes.data);
      toast.success(<span data-testid="reviews-saved-toast">Recensioni aggiornate — sono già sul sito</span>);
    } catch {
      toast.error("Salvataggio non riuscito");
    } finally {
      setSaving(false);
    }
  };

  if (!reviews || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D0C0A]">
        <p className="font-cinzel tracking-[0.3em] text-xs uppercase text-[#C9A265]">Caricamento recensioni...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-reviews-editor" className="min-h-screen bg-[#0D0C0A]">
      <header className="border-b border-[rgba(201,162,101,0.18)] bg-[#171512]/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-9 w-9" />
            <div>
              <p className="font-cinzel text-sm tracking-[0.25em] text-[#FAF7F2]">RECENSIONI</p>
              <p className="text-[11px] text-[#7A7267]">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/admin"
              data-testid="reviews-back-to-reservations"
              className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#B0A89C] hover:text-[#C9A265] font-cinzel transition-colors"
            >
              <ArrowLeft size={14} /> Prenotazioni
            </a>
            <button
              data-testid="reviews-save-button"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-[#C9A265] text-[#0D0C0A] text-xs uppercase tracking-[0.2em] font-semibold font-cinzel rounded-full hover:bg-[#E0B87A] transition-colors duration-300 disabled:opacity-50"
            >
              <Save size={14} /> {saving ? "Salvataggio..." : "Salva"}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
        <p className="text-sm text-[#7A7267] mb-8 max-w-xl leading-relaxed">
          Le citazioni che vedi qui compaiono nella sezione "Dicono di noi" del sito, in italiano e in inglese.
        </p>

        <div className="bg-[#171512] border border-[rgba(201,162,101,0.25)] rounded-xl p-5 mb-10">
          <label className={labelCls}>Link del pulsante "Lasciaci una recensione su Google"</label>
          <input
            data-testid="google-review-url-input"
            value={settings.google_review_url || ""}
            onChange={(e) => setSettings({ ...settings, google_review_url: e.target.value })}
            placeholder="https://g.page/r/..."
            className={inputCls}
          />
          <p className="mt-2 text-[11px] text-[#7A7267] leading-relaxed">
            Dove trovarlo: apri il tuo profilo Google Business → "Chiedi una recensione" → copia il link e incollalo qui.
            Se il campo è vuoto, il pulsante sul sito porta alla ricerca Google del ristorante.
          </p>
        </div>

        <div className="space-y-4">
          {reviews.map((r, i) => (
            <motion.div
              key={i}
              data-testid={`review-editor-${i}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-[#171512] border border-[rgba(255,255,255,0.08)] rounded-xl p-5 space-y-3"
            >
              <div className="grid sm:grid-cols-[1fr_1fr_110px_44px] gap-3 items-end">
                <div>
                  <label className={labelCls}>Nome cliente</label>
                  <input
                    data-testid={`review-name-${i}`}
                    value={r.name}
                    onChange={(e) => update((rs) => { rs[i].name = e.target.value; })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Fonte (es. Google)</label>
                  <input
                    data-testid={`review-source-${i}`}
                    value={r.source || ""}
                    onChange={(e) => update((rs) => { rs[i].source = e.target.value; })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Stelle (1-5)</label>
                  <input
                    data-testid={`review-stars-${i}`}
                    type="number"
                    min={1}
                    max={5}
                    value={r.stars || 5}
                    onChange={(e) => update((rs) => { rs[i].stars = Math.max(1, Math.min(5, parseInt(e.target.value, 10) || 5)); })}
                    className={inputCls}
                  />
                </div>
                <button
                  data-testid={`review-delete-${i}`}
                  onClick={() => update((rs) => { rs.splice(i, 1); })}
                  className="h-[46px] w-[44px] rounded-xl border border-red-800/50 text-red-300 flex items-center justify-center hover:bg-red-950/50 transition-colors"
                  title="Elimina"
                >
                  <Trash2 size={16} />
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Testo (IT)</label>
                  <textarea
                    data-testid={`review-text-it-${i}`}
                    rows={3}
                    value={r.text?.it || ""}
                    onChange={(e) => update((rs) => { rs[i].text = { ...(rs[i].text || {}), it: e.target.value }; })}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Testo (EN)</label>
                  <textarea
                    data-testid={`review-text-en-${i}`}
                    rows={3}
                    value={r.text?.en || ""}
                    onChange={(e) => update((rs) => { rs[i].text = { ...(rs[i].text || {}), en: e.target.value }; })}
                    className={inputCls}
                  />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <button
          data-testid="review-add-button"
          onClick={() => update((rs) => { rs.push({ name: "", source: "", stars: 5, text: { it: "", en: "" } }); })}
          className="mt-4 flex items-center gap-2 px-5 py-3 border border-dashed border-[rgba(201,162,101,0.4)] text-[#C9A265] text-xs uppercase tracking-[0.2em] font-cinzel rounded-xl hover:bg-[#C9A265]/10 transition-colors"
        >
          <Plus size={14} /> Aggiungi recensione
        </button>
      </main>
    </div>
  );
}
